/**
 * Preferred brand order for equipment pickers
 * (heads / shafts / grips / rangefinders / GPS / launch monitors).
 * Matching items appear first (in this order); remaining options stay A–Z.
 *
 * Catalog spellings often differ from marketing names — keep alias groups in sync
 * with clubclass / shafts / grips / accessory class seed data.
 */

export type BrandCategory =
  | 'club'
  | 'shaft'
  | 'grip'
  | 'rangefinder'
  | 'gps'
  | 'launch_monitor';

/** Canonical display labels (order = dropdown pin order). */
export const CLUB_HEAD_BRAND_PRIORITY = [
  'Titleist',
  'TaylorMade',
  'Callaway',
  'Ping',
  'Mizuno',
  'Cobra',
  'Srixon / Cleveland',
  'PXG',
  'Wilson',
  'Bridgestone',
  'Scotty Cameron',
  'Vokey',
  'Odyssey',
  'Bettinardi',
  'L.A.B. Golf',
  'Evnroll',
] as const;

export const SHAFT_BRAND_PRIORITY = [
  'Fujikura',
  'Mitsubishi Chemical (MCA)',
  'True Temper',
  'Project X',
  'Graphite Design',
  'KBS',
  'Nippon',
  'Aldila',
  'UST Mamiya',
  'LA Golf',
] as const;

export const GRIP_BRAND_PRIORITY = [
  'Golf Pride',
  'Lamkin',
  'SuperStroke',
  'Winn',
  'Iomic',
  'JumboMax',
  'PURE',
  'Champ',
  'Karma',
  'Flat Cat',
] as const;

/** Laser rangefinders — Core Register + Pro marketplace. */
export const RANGEFINDER_BRAND_PRIORITY = [
  'Bushnell',
  'Nikon',
  'Garmin',
  'Precision Pro',
  'Blue Tees',
  'Shot Scope',
] as const;

/** GPS devices / watches. */
export const GPS_BRAND_PRIORITY = [
  'Garmin',
  'Bushnell',
  'Shot Scope',
  'SkyCaddie',
  'GolfBuddy',
  'Voice Caddie',
] as const;

/** Simulators / launch monitors. */
export const LAUNCH_MONITOR_BRAND_PRIORITY = [
  'TrackMan',
  'Foresight Sports',
  'FlightScope',
  'Garmin',
  'SkyTrak',
  'Rapsodo',
] as const;

/** @deprecated Prefer CLUB_HEAD_BRAND_PRIORITY */
export const PRIORITY_CLUB_BRANDS = CLUB_HEAD_BRAND_PRIORITY;
/** @deprecated Prefer SHAFT_BRAND_PRIORITY */
export const PRIORITY_SHAFT_BRANDS = SHAFT_BRAND_PRIORITY;
/** @deprecated Prefer GRIP_BRAND_PRIORITY */
export const PRIORITY_GRIP_BRANDS = GRIP_BRAND_PRIORITY;

export const BRAND_PRIORITY_BY_CATEGORY: Record<
  BrandCategory,
  readonly string[]
> = {
  club: CLUB_HEAD_BRAND_PRIORITY,
  shaft: SHAFT_BRAND_PRIORITY,
  grip: GRIP_BRAND_PRIORITY,
  rangefinder: RANGEFINDER_BRAND_PRIORITY,
  gps: GPS_BRAND_PRIORITY,
  launch_monitor: LAUNCH_MONITOR_BRAND_PRIORITY,
};

/** Alias groups — first string is the preferred match key; rest are DB/catalog variants. */
const CLUB_ALIAS_GROUPS: string[][] = [
  ['Titleist'],
  ['TaylorMade', 'Taylor Made', 'Taylormade'],
  ['Callaway'],
  ['Ping', 'PING'],
  ['Mizuno'],
  ['Cobra', 'COBRA'],
  ['Srixon / Cleveland', 'Srixon', 'Srixon Golf', 'Cleveland', 'Cleveland Golf'],
  ['PXG', 'Parsons Xtreme Golf'],
  ['Wilson', 'Wilson Staff', 'Wilson Golf'],
  ['Bridgestone', 'Bridgestone Golf'],
  ['Scotty Cameron', 'Scotty Cameron Designs', 'Titleist Putter'],
  ['Vokey', 'Bob Vokey Designs', 'Titleist Wedge', 'Vokey Design'],
  ['Odyssey', 'Odyssey Golf', 'Callaway Putter'],
  ['Bettinardi'],
  ['L.A.B. Golf', 'LAB Golf', 'L.A.B Golf', 'LAB'],
  ['Evnroll', 'EVNROLL'],
];

const SHAFT_ALIAS_GROUPS: string[][] = [
  ['Fujikura'],
  [
    'Mitsubishi Chemical (MCA)',
    'Mitsubishi Chemical',
    'Mitsubishi',
    'MCA',
    'Mitsubishi Rayon',
    'Miyazaki',
  ],
  ['True Temper', 'Truetemper', 'TrueTemper'],
  ['Project X', 'ProjectX', 'PX'],
  ['Graphite Design', 'Graphite Designs'],
  ['KBS'],
  ['Nippon', 'Nippon Shaft', 'N.S. Pro', 'NS Pro'],
  ['Aldila'],
  ['UST Mamiya', 'UST', 'Ust Mamiya', 'Recoil'],
  ['LA Golf', 'L.A. Golf', 'LAGP'],
];

const GRIP_ALIAS_GROUPS: string[][] = [
  ['Golf Pride', 'Golfpride'],
  ['Lamkin'],
  ['SuperStroke', 'Superstroke', 'Super Stroke'],
  ['Winn'],
  ['Iomic', 'IOMIC'],
  ['JumboMax', 'Jumbo Max'],
  ['PURE', 'Pure Grips', 'Pure'],
  ['Champ', 'Champ Grips'],
  ['Karma'],
  ['Flat Cat', 'FlatCat'],
];

const RANGEFINDER_ALIAS_GROUPS: string[][] = [
  ['Bushnell'],
  ['Nikon'],
  ['Garmin'],
  ['Precision Pro', 'PrecisionPro'],
  ['Blue Tees', 'BlueTees', 'Blue Tee'],
  ['Shot Scope', 'ShotScope', 'Shotscope'],
];

const GPS_ALIAS_GROUPS: string[][] = [
  ['Garmin'],
  ['Bushnell'],
  ['Shot Scope', 'ShotScope', 'Shotscope'],
  ['SkyCaddie', 'Sky Caddie', 'Skycaddie'],
  ['GolfBuddy', 'Golf Buddy', 'Golfbuddy'],
  ['Voice Caddie', 'VoiceCaddie', 'Voice caddie'],
];

const LAUNCH_MONITOR_ALIAS_GROUPS: string[][] = [
  ['TrackMan', 'Trackman', 'Track Man'],
  ['Foresight Sports', 'Foresight', 'Foresightsports'],
  ['FlightScope', 'Flightscope', 'Flight Scope'],
  ['Garmin'],
  ['SkyTrak', 'Skytrak', 'Sky Trak'],
  ['Rapsodo'],
];

const ALIAS_GROUPS: Record<BrandCategory, string[][]> = {
  club: CLUB_ALIAS_GROUPS,
  shaft: SHAFT_ALIAS_GROUPS,
  grip: GRIP_ALIAS_GROUPS,
  rangefinder: RANGEFINDER_ALIAS_GROUPS,
  gps: GPS_ALIAS_GROUPS,
  launch_monitor: LAUNCH_MONITOR_ALIAS_GROUPS,
};

export function normalizeBrandKey(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * How well a catalog brand matches one alias.
 *
 * Prefer the most specific hit — first-group-wins + loose prefix matching
 * let parent pins (Titleist / Callaway) steal legacy sub-brand spellings
 * that belong later in the list (`Titleist Wedge` → Vokey,
 * `Titleist Putter` → Scotty Cameron, `Callaway Putter` → Odyssey).
 */
function aliasMatchScore(brandKey: string, aliasKey: string): number | null {
  if (!brandKey || !aliasKey) return null;
  if (brandKey === aliasKey) {
    // Exact match always wins over any prefix association.
    return 10_000 + aliasKey.length;
  }
  if (brandKey.startsWith(`${aliasKey} `)) {
    // Catalog is more specific than the pin alias ("Titleist TSR",
    // "Scotty Cameron Designs" vs pin "Scotty Cameron"). Longer alias
    // beats a shorter parent when both match.
    return 1_000 + aliasKey.length;
  }
  if (aliasKey.startsWith(`${brandKey} `)) {
    // Catalog is the short form of a longer alias ("Scotty Cameron"
    // vs alias "Scotty Cameron Designs"). Weakest — must not outrank
    // an exact/parent match for a different pin.
    return 100 + brandKey.length;
  }
  return null;
}

/** Flat list of every priority label + alias (for debug / migration helpers). */
export function flatPriorityLabels(category: BrandCategory): string[] {
  return ALIAS_GROUPS[category].flat();
}

/**
 * Priority rank for a brand string within a category (0 = first pin).
 * Returns null when the brand is not in the priority list.
 */
export function getBrandPriorityRank(
  brand: string,
  category: BrandCategory,
): number | null {
  const hit = getBrandPriorityMatch(brand, category);
  return hit ? hit.rank : null;
}

function getBrandPriorityMatch(
  brand: string,
  category: BrandCategory,
): { rank: number; aliasIndex: number } | null {
  const brandKey = normalizeBrandKey(brand);
  if (!brandKey) return null;

  const groups = ALIAS_GROUPS[category];
  let best: { rank: number; aliasIndex: number; score: number } | null = null;

  for (let i = 0; i < groups.length; i++) {
    for (let j = 0; j < groups[i].length; j++) {
      const aliasKey = normalizeBrandKey(groups[i][j]);
      const score = aliasMatchScore(brandKey, aliasKey);
      if (score === null) continue;
      if (!best || score > best.score) {
        best = { rank: i, aliasIndex: j, score };
      }
    }
  }

  return best ? { rank: best.rank, aliasIndex: best.aliasIndex } : null;
}

/**
 * Sort brand names: pinned priority order first, then A–Z for the rest.
 * Only brands present in `brands` are returned (missing catalog entries are skipped).
 * When multiple catalog spellings map to the same pin (e.g. Srixon + Cleveland),
 * alias order within the group decides which appears first.
 */
export function sortBrandsForCategory(
  brands: readonly string[],
  category: BrandCategory,
): string[] {
  const unique = Array.from(
    new Set(brands.map((b) => (typeof b === 'string' ? b.trim() : '')).filter(Boolean)),
  );

  const priority: { brand: string; rank: number; aliasIndex: number }[] = [];
  const rest: string[] = [];

  for (const brand of unique) {
    const hit = getBrandPriorityMatch(brand, category);
    if (hit === null) {
      rest.push(brand);
    } else {
      priority.push({ brand, rank: hit.rank, aliasIndex: hit.aliasIndex });
    }
  }

  priority.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    if (a.aliasIndex !== b.aliasIndex) return a.aliasIndex - b.aliasIndex;
    return a.brand.localeCompare(b.brand, undefined, { sensitivity: 'base' });
  });
  rest.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  return [...priority.map((p) => p.brand), ...rest];
}

/** Alias for callers that prefer "ByPriority" naming. */
export function sortBrandsByPriority(
  brands: readonly string[],
  category: BrandCategory,
): string[] {
  return sortBrandsForCategory(brands, category);
}

/**
 * Split a brand list into priority vs alphabetical remainder (for UI with a divider).
 */
export function partitionBrandsByPriority(
  brands: readonly string[],
  category: BrandCategory,
): { priority: string[]; rest: string[] } {
  const sorted = sortBrandsForCategory(brands, category);
  const priority: string[] = [];
  const rest: string[] = [];
  for (const brand of sorted) {
    if (getBrandPriorityRank(brand, category) !== null) {
      priority.push(brand);
    } else {
      rest.push(brand);
    }
  }
  return { priority, rest };
}
